import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { Capacitor } from '@capacitor/core';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';
import { captureException } from '../../services/SentryService';

// No WebSocket exists for the lobby's public-rooms list — same polling gap
// documented in WaitingRoomScreen.jsx, just on a more relaxed cadence since
// there's no single room whose state the viewer is waiting to act on.
const PUBLIC_ROOMS_POLL_INTERVAL_MS = 5000;

function gameModeLabel(t, room) {
    if (room.gameMode === 'CLASSIC') return t('setup.game_mode_classic');
    return room.gameType === 'BACIGA' ? t('online.lobby.game_type_baciga') : t('online.lobby.game_type_ronda');
}

export default function OnlineLobbyScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authFetch, user, logout } = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [activeRoom, setActiveRoom] = useState(null);
    const [joinCode, setJoinCode] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const [publicRooms, setPublicRooms] = useState([]);
    const [isLoadingPublicRooms, setIsLoadingPublicRooms] = useState(true);
    const [isRefreshingPublicRooms, setIsRefreshingPublicRooms] = useState(false);

    // Public games already under way, joinable only as a read-only spectator
    // (GET /rooms/live — only rooms with a session actually running).
    const [liveRooms, setLiveRooms] = useState([]);

    const [lobbyStats, setLobbyStats] = useState(null);

    // Create/join actions abandon any room the user is already in — gate
    // them behind a confirmation instead of just a passive hint (see
    // handleAbandonConfirmed / runOrConfirmAbandon below).
    const [pendingAction, setPendingAction] = useState(null);

    const reportError = useCallback(
        (err) => setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined))),
        [t]
    );

    const loadMyRoom = useCallback(async () => {
        setIsLoading(true);
        try {
            const room = await authFetch('/rooms/mine');
            setActiveRoom(room);
        } catch (err) {
            if (err instanceof ApiError && err.code === 'ROOM_NOT_FOUND') {
                setActiveRoom(null);
            } else {
                reportError(err);
            }
        } finally {
            setIsLoading(false);
        }
    }, [authFetch, reportError]);

    useEffect(() => {
        loadMyRoom();
    }, [loadMyRoom]);

    const loadPublicRooms = useCallback(async (isManual = false) => {
        if (isManual) setIsRefreshingPublicRooms(true);
        try {
            const result = await authFetch('/rooms/public?limit=20&offset=0');
            setPublicRooms(result.items);
        } catch (err) {
            // Kept quiet in the UI: this list refreshes on its own cadence and
            // isn't the primary action on this screen — no need to interrupt
            // the user with an error banner over a background poll. Still
            // reported so silent failures (e.g. a cold-started backend) don't
            // go completely unnoticed.
            captureException(err, { context: 'OnlineLobbyScreen.loadPublicRooms', isManual });
        } finally {
            setIsLoadingPublicRooms(false);
            if (isManual) setIsRefreshingPublicRooms(false);
        }
    }, [authFetch]);

    useEffect(() => {
        loadPublicRooms();
        const interval = setInterval(loadPublicRooms, PUBLIC_ROOMS_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [loadPublicRooms]);

    const loadLiveRooms = useCallback(async () => {
        try {
            const result = await authFetch('/rooms/live');
            setLiveRooms(result.items);
        } catch (err) {
            // Same rationale as loadPublicRooms: a background list shouldn't
            // interrupt the user with an error banner.
            captureException(err, { context: 'OnlineLobbyScreen.loadLiveRooms' });
        }
    }, [authFetch]);

    useEffect(() => {
        loadLiveRooms();
        const interval = setInterval(loadLiveRooms, PUBLIC_ROOMS_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [loadLiveRooms]);

    // Same polling gap/cadence as loadPublicRooms above — this widget has no
    // WebSocket push either, and shares its rhythm rather than adding a
    // second independent timer for numbers that update at the same pace.
    const loadLobbyStats = useCallback(async () => {
        try {
            const stats = await authFetch('/stats/lobby');
            setLobbyStats(stats);
        } catch (err) {
            // Same rationale as loadPublicRooms: a background stats widget
            // shouldn't interrupt the user with an error banner.
            captureException(err, { context: 'OnlineLobbyScreen.loadLobbyStats' });
        }
    }, [authFetch]);

    useEffect(() => {
        loadLobbyStats();
        const interval = setInterval(loadLobbyStats, PUBLIC_ROOMS_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [loadLobbyStats]);

    // The 5s poll above is driven by setInterval, which mobile OSes suspend
    // while the app is backgrounded — coming back from another app can leave
    // the list stale well past the poll interval until this fires. Refetch
    // immediately whenever the app (or browser tab) becomes active again.
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            let appListener = null;
            let cancelled = false;
            (async () => {
                const { App } = await import('@capacitor/app');
                if (cancelled) return;
                appListener = await App.addListener('appStateChange', ({ isActive }) => {
                    if (isActive) {
                        loadPublicRooms();
                        loadLiveRooms();
                        loadLobbyStats();
                    }
                });
            })();
            return () => {
                cancelled = true;
                if (appListener) appListener.remove();
            };
        }

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                loadPublicRooms();
                loadLiveRooms();
                loadLobbyStats();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [loadPublicRooms, loadLiveRooms, loadLobbyStats]);

    // The viewer's own game isn't something to "watch" — it has its own card above.
    const watchableRooms = liveRooms.filter((room) => room.id !== activeRoom?.id);

    const isHostOfActiveRoom = Boolean(activeRoom && user && activeRoom.hostUserId === user.id);
    const activeRoomIsSoloVsAi = Boolean(
        activeRoom && activeRoom.seats.every((s) => s.occupantType !== 'HUMAN' || s.userId === user?.id)
    );
    // Mirrors the backend gate in room.service.ts's cancel(): the host can
    // always cancel a room still WAITING/STARTING, and can also cancel a
    // live one if every other seat is already AI — there's no one else's
    // game to disrupt.
    const canCancelActiveRoom = Boolean(
        activeRoom &&
            isHostOfActiveRoom &&
            (activeRoom.status === 'WAITING' || activeRoom.status === 'STARTING' || activeRoomIsSoloVsAi)
    );

    const handleCancelActiveRoom = async () => {
        if (!activeRoom) return;
        setError(null);
        setIsBusy(true);
        try {
            await authFetch(`/rooms/${activeRoom.id}/cancel`, { method: 'POST' });
            setActiveRoom(null);
        } catch (err) {
            reportError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const joinByCode = async (code) => {
        if (!code.trim()) return;
        setError(null);
        setIsBusy(true);
        try {
            const room = await authFetch(`/rooms/${encodeURIComponent(code.trim())}/join`, { method: 'POST' });
            navigate(`/online/room/${room.id}`);
        } catch (err) {
            reportError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const handleJoinRoom = (event) => {
        event.preventDefault();
        runOrConfirmAbandon(() => joinByCode(joinCode));
    };

    const runOrConfirmAbandon = (action) => {
        if (activeRoom) {
            setPendingAction(() => action);
        } else {
            action();
        }
    };

    const handleAbandonConfirmed = () => {
        const action = pendingAction;
        setPendingAction(null);
        if (action) action();
    };

    const handleLogoutConfirmed = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            navigate('/online');
        } finally {
            setIsLoggingOut(false);
            setLogoutConfirmOpen(false);
        }
    };

    return (
        <>
            <Header
                title={t('online.lobby.title')}
                onBack={() => navigate('/')}
                showSettings={false}
                onHistory={() => navigate('/online/history')}
                onRankings={() => navigate('/online/rankings')}
                onProfile={() => navigate('/online/profile')}
                onLogout={() => setLogoutConfirmOpen(true)}
            />
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}

                    {lobbyStats && (
                        <Card elevation={3}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" spacing={2} justifyContent="space-around" flexWrap="wrap">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <PeopleAltIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            {t('online.lobby.stats_online_users', { count: lobbyStats.onlineUsers })}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <SportsEsportsIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            {t('online.lobby.stats_games_in_progress', { count: lobbyStats.gamesInProgress })}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <HourglassTopIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            {t('online.lobby.stats_waiting_room_users', { count: lobbyStats.waitingRoomUsers })}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                {/* Left column: active room, create, join by code */}
                                <Grid item xs={12} sm={5}>
                                    <Stack spacing={2}>
                                        {activeRoom && (
                                            <Card elevation={3}>
                                                <CardContent>
                                                    <Typography variant="h6">{t('online.lobby.active_room_title')}</Typography>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {activeRoom.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('online.room.code_label')}: <strong>{activeRoom.code}</strong>
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {gameModeLabel(t, activeRoom)}
                                                        {' · '}
                                                        {t('online.lobby.game_points_short', { points: activeRoom.gamePoints })}
                                                    </Typography>
                                                </CardContent>
                                                <CardActions>
                                                    <Button
                                                        variant="contained"
                                                        fullWidth
                                                        onClick={() => navigate(`/online/room/${activeRoom.id}`)}
                                                    >
                                                        {t('online.lobby.go_to_room')}
                                                    </Button>
                                                    {canCancelActiveRoom && (
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            fullWidth
                                                            disabled={isBusy}
                                                            onClick={handleCancelActiveRoom}
                                                        >
                                                            {t('online.lobby.cancel_room_button')}
                                                        </Button>
                                                    )}
                                                </CardActions>
                                            </Card>
                                        )}

                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            onClick={() => runOrConfirmAbandon(() => navigate('/online/create'))}
                                        >
                                            {t('online.lobby.create_room_button')}
                                        </Button>

                                        <Card elevation={3}>
                                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('online.lobby.join_room_title')}
                                                </Typography>
                                                <Box
                                                    component="form"
                                                    onSubmit={handleJoinRoom}
                                                    sx={{ display: 'flex', gap: 1 }}
                                                >
                                                    <TextField
                                                        size="small"
                                                        label={t('online.room.join_code_placeholder')}
                                                        value={joinCode}
                                                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                                        fullWidth
                                                        inputProps={{ maxLength: 12, style: { textTransform: 'uppercase' } }}
                                                    />
                                                    <Button type="submit" variant="contained" size="small" disabled={isBusy || !joinCode.trim()}>
                                                        {t('online.room.join_button')}
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Stack>
                                </Grid>

                                {/* Right column: public rooms list */}
                                <Grid item xs={12} sm={7}>
                                    <Card elevation={3} sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                                                    {t('online.lobby.public_rooms_title')}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    aria-label={t('online.lobby.public_rooms_refresh')}
                                                    onClick={() => loadPublicRooms(true)}
                                                    disabled={isLoadingPublicRooms || isRefreshingPublicRooms}
                                                >
                                                    {isRefreshingPublicRooms ? (
                                                        <CircularProgress size={18} />
                                                    ) : (
                                                        <RefreshIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Box>

                                            {isLoadingPublicRooms ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                                    <CircularProgress size={28} />
                                                </Box>
                                            ) : publicRooms.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('online.lobby.public_rooms_empty')}
                                                </Typography>
                                            ) : (
                                                <List disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
                                                    {publicRooms.map((room) => (
                                                        <ListItem
                                                            key={room.id}
                                                            divider
                                                            sx={{ px: 0 }}
                                                            secondaryAction={
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    disabled={isBusy}
                                                                    onClick={() => runOrConfirmAbandon(() => joinByCode(room.code))}
                                                                >
                                                                    {t('online.room.join_button')}
                                                                </Button>
                                                            }
                                                        >
                                                            <Avatar sx={{ mr: 2, width: 32, height: 32, bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                                                                <GroupsIcon fontSize="small" />
                                                            </Avatar>
                                                            <ListItemText
                                                                primary={room.name}
                                                                secondary={
                                                                    <>
                                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                                                            {gameModeLabel(t, room)} · {t('online.lobby.game_points_short', { points: room.gamePoints })}
                                                                        </Typography>
                                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                            {t('online.lobby.public_rooms_seats', { count: room.humanCount })}
                                                                            {', '}
                                                                            {t('online.lobby.public_rooms_created_by', {
                                                                                name: room.hostUserName,
                                                                                time: new Date(room.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                                                            })}
                                                                        </Typography>
                                                                    </>
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}
                                        </CardContent>
                                    </Card>

                                    <Card elevation={3} sx={{ mt: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {t('online.lobby.live_rooms_title')}
                                            </Typography>
                                            {watchableRooms.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('online.lobby.live_rooms_empty')}
                                                </Typography>
                                            ) : (
                                                <List disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
                                                    {watchableRooms.map((room) => (
                                                        <ListItem
                                                            key={room.id}
                                                            divider
                                                            sx={{ px: 0 }}
                                                            secondaryAction={
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<VisibilityIcon fontSize="small" />}
                                                                    onClick={() => navigate(`/online/spectate/${room.id}`)}
                                                                >
                                                                    {t('online.lobby.watch_button')}
                                                                </Button>
                                                            }
                                                        >
                                                            <Avatar sx={{ mr: 2, width: 32, height: 32, bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                                                                <SportsEsportsIcon fontSize="small" />
                                                            </Avatar>
                                                            <ListItemText
                                                                primary={room.name}
                                                                secondary={
                                                                    <>
                                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                                                            {gameModeLabel(t, room)} · {t('online.lobby.game_points_short', { points: room.gamePoints })}
                                                                            {' · '}
                                                                            {t('online.lobby.live_rooms_score', { team1: room.matchScoreTeam1, team2: room.matchScoreTeam2 })}
                                                                        </Typography>
                                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                            {room.seats
                                                                                .filter((seat) => seat.occupantType === 'HUMAN')
                                                                                .map((seat) => seat.userName)
                                                                                .join(', ')}
                                                                            {room.spectatorCount > 0 &&
                                                                                ` · ${t('online.lobby.live_rooms_spectators', { count: room.spectatorCount })}`}
                                                                        </Typography>
                                                                    </>
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Stack>
            </Container>

            <Dialog open={Boolean(pendingAction)} onClose={() => setPendingAction(null)}>
                <DialogTitle>{t('online.lobby.abandon_confirm_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('online.lobby.abandon_confirm_message')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingAction(null)}>{t('online.lobby.abandon_confirm_cancel')}</Button>
                    <Button color="error" onClick={handleAbandonConfirmed}>
                        {t('online.lobby.abandon_confirm_confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={logoutConfirmOpen} onClose={() => (isLoggingOut ? null : setLogoutConfirmOpen(false))}>
                <DialogTitle>{t('online.lobby.logout_confirm_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('online.lobby.logout_confirm_message')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogoutConfirmOpen(false)} disabled={isLoggingOut}>
                        {t('online.lobby.logout_confirm_cancel')}
                    </Button>
                    <Button color="error" onClick={handleLogoutConfirmed} disabled={isLoggingOut}>
                        {t('online.lobby.logout_confirm_confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
